"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { UploadStatus } from "@/lib/postRaw";
import { FileChip } from "./FileChip";
import { cn } from "./ui/cn";

/**
 * A video attachment, played ON DEMAND.
 *
 * NOTHING AUTOPLAYS. `autoPlay` is absent on purpose and must stay absent: a
 * feed is a column of many posts, and a video that starts by itself plays
 * sound the reader did not ask for, burns mobile data on a post they scrolled
 * past, and — with several in one feed — plays over the top of each other
 * (OSO-437). The controls are the whole interface: press play, or don't.
 *
 * `preload="metadata"` fetches the header, not the file: enough for the
 * duration and the scrubber, without pulling ~100 MB of phone video down the
 * wire for a post nobody opens. `playsInline` keeps iOS Safari from throwing
 * the clip into its own fullscreen player the moment it starts, which is what
 * it does by default for an inline `<video>`.
 *
 * TWO SOURCES, ONE PLAYER — OSO-645 V8. A Bunny-hosted video carries an `hlsUrl`
 * (adaptive) as well as `src` (a single-file MP4). The rule, in order:
 *   1. `hlsUrl` where the browser plays HLS natively (iOS/macOS Safari) — no
 *      library, `<video src={hls}>` just works.
 *   2. `hlsUrl` through hls.js everywhere else (Chrome, Firefox, Android), so
 *      the viewer gets the bitrate ladder on mobile data instead of buffer-or-
 *      nothing.
 *   3. `src` (the MP4) as the last resort — a local video has only this, and it
 *      is also where a Bunny video lands if hls.js throws a fatal error.
 * A LOCAL video has no `hlsUrl`, so it takes arm 3 unchanged: `<video src={mp4}>`
 * exactly as before. The client never learns which host a video lives on; it
 * only sees whether an `hlsUrl` is present.
 *
 * THE POSTER IS THE TRANSCODE JOB'S (local) OR BUNNY'S THUMBNAIL. Either way it
 * is `posterUrl` on the attachment (media-pipeline §3.6 / V7), so the player has
 * something to show before play instead of a black rectangle.
 *
 * IT IS ALSO WHERE A VIDEO THAT IS NOT THERE YET IS DRAWN — OSO-490. `status`
 * comes off the attachment: `processing` is the worker still encoding, and the
 * player becomes a box of the same size with a spinner, so the card does not
 * jump when the clip arrives; `failed` is a transcode that will never finish,
 * and it becomes an error chip with `onRemove` — "Fjern video" — which is the
 * author's way out. Neither state reaches anybody but the author.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE PLAYER IS THE CLIP'S OWN SHAPE, INSIDE A PHOTO'S BUDGET — OSO-665
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * V8 sized the element to the video and capped it at `max-h-[70vh]`, which on
 * a laptop is ~890 px — nearly twice the 480 px a single photo gets in the same
 * feed (`RichContent`'s `single`). A portrait clip took over the screen, and it
 * had BLACK BARS anyway, because a full-width element whose height is clamped
 * is no longer the video's shape.
 *
 * So the height budget and the shape are decided together, on a FRAME the video
 * fills absolutely:
 *
 *   height ≤ maxHeight       the caller's budget — a photo's, per surface
 *   width  ≤ maxHeight × r   so the frame IS the clip's rectangle, not a
 *                            letterbox of it. A 9:16 clip in a 480 px budget
 *                            is 270×480 and centred; a 16:9 clip in a 640 px
 *                            column is 640×360, as before.
 *
 * `r` comes from `width`/`height` on the attachment — the wire has carried them
 * since the pipeline was built (ffprobe writes them for a local video, Bunny's
 * metadata for a hosted one). When they are missing (Bunny can answer without
 * them) the element measures itself on `loadedmetadata` and the frame corrects.
 *
 * `maxHeight: null` opts out of the frame entirely and hands sizing back to
 * `className` — the lightbox, where the video is the whole point of the screen
 * and a 480 px cap would be absurd.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NOTHING JUMPS, AND NOTHING IS A BLACK SLAB WHILE IT LOADS — OSO-665
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The frame carries the aspect ratio, so its box is reserved before a single
 * byte of video or poster arrives, and the card below it does not move when
 * metadata lands.
 *
 * `processing` uses the SAME frame rather than the hardcoded `aspect-video` it
 * used to, so the two states agree whenever they are given the same numbers.
 * THEY ARE NOT YET, and this is the honest limit of it: a `processing` row
 * carries `width: null` — `finishProcessing` writes the dimensions in the very
 * UPDATE that flips the status to `ready` — so the placeholder is always the
 * 16:9 fallback, and a portrait clip's card still moves once when the
 * transcode lands. Closing that means probing on receipt rather than on
 * completion, which is a backend change and not this one.
 *
 * Inside the reserved box, a shimmer stands in until the poster has actually
 * decoded, rather than showing the `<video>`'s black backdrop. It is
 * `pointer-events-none`, so pressing play through it works on the first frame
 * it is up.
 */

/** The shape we reserve before anything has told us the real one. Most video
 *  is landscape, so guessing 16:9 is the guess that is usually already right. */
const FALLBACK_RATIO = 16 / 9;

/** A single photo's budget in a post (`RichContent`'s `single`). Callers that
 *  have their own surface pass their own; this is what "a video in a column of
 *  content" costs when nobody says otherwise. */
const DEFAULT_MAX_HEIGHT = 480;

/** The reserved box: the clip's shape, centred in whatever column it is in.
 *  `--video-aspect` is set per render — see `frameStyle`. */
const FRAME_SHAPE =
  "relative mx-auto w-full aspect-[var(--video-aspect)] rounded-lg overflow-hidden";

/** The clip's width ÷ height, or null when we have not been told a usable pair. */
function ratioOf(width?: number | null, height?: number | null): number | null {
  if (!width || !height || width <= 0 || height <= 0) return null;
  return width / height;
}

export function VideoPlayer({
  src,
  hlsUrl,
  filename,
  poster,
  width,
  height,
  maxHeight = DEFAULT_MAX_HEIGHT,
  status = "ready",
  onRemove,
  removeLabel = "Fjern video",
  removeBusy = false,
  className,
}: {
  /** The MP4 URL. Read at render time — protected-tier urls are signed and
   *  expire. `null` when `status` is not `ready`; there is nothing to serve. */
  src: string | null;
  /** The adaptive HLS URL for a Bunny video, or undefined for a local one. */
  hlsUrl?: string;
  /** Shown if the browser cannot play it, and in both non-ready states. */
  filename?: string;
  /** The still, or undefined. A 404 here is harmless — the browser shows black. */
  poster?: string;
  /** The clip's pixel dimensions off the attachment. Either being missing just
   *  means the frame is measured from the element instead of reserved exactly. */
  width?: number | null;
  height?: number | null;
  /** The player's vertical budget in px, or `null` to size it from `className`
   *  alone (the lightbox). See the frame section above. */
  maxHeight?: number | null;
  /** The upload's state (OSO-490). Defaults to `ready`. */
  status?: UploadStatus;
  /** The action on the `failed` state. Omitted for a viewer who may not edit. */
  onRemove?: () => void;
  /** What that action WILL DO — "Fjern video", or a delete when the row is only
   *  this video (a video-only comment cannot be edited down to empty). */
  removeLabel?: string;
  /** In flight. The button stays PRESENT and goes disabled. */
  removeBusy?: boolean;
  /** Extra classes. They land on the frame — which IS the player's box — or on
   *  the `<video>` itself when `maxHeight` is null and there is no frame. */
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  /**
   * The ratio the ELEMENT reported. Neither this nor `posterLoaded` is reset
   * when the url changes, DELIBERATELY: a media url is signed and hour-rounded
   * (`lib/attachments.ts`), so `src` and `poster` change on the hour for a clip
   * nobody touched, and keying a reset — or a React `key` — on one would blank
   * the shimmer and re-measure mid-playback every hour. The cost of not
   * resetting is confined to a post edited to SWAP its clip, where the new
   * still skips its shimmer once; the ratio re-measures on its own, because a
   * changed `src` reloads the element and fires `loadedmetadata` again.
   */
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);
  const [posterLoaded, setPosterLoaded] = useState(false);

  /**
   * ATTACH hls.js ONLY WHEN WE NEED IT — arm 2 above. It is loaded dynamically
   * so its ~130 KB never enters the bundle of a page with no Bunny video on it,
   * and it is torn down on unmount or a source change so a feed scrolled past
   * does not leak decoders. Native-HLS browsers and local MP4s never reach it.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || status !== "ready" || !hlsUrl) return;
    // Native HLS (Safari): let the element load it directly.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
      return;
    }
    let hls: import("hls.js").default | null = null;
    let cancelled = false;
    void import("hls.js")
      .then(({ default: Hls }) => {
      if (cancelled || !videoRef.current) return;
      if (!Hls.isSupported()) {
        // No MSE and no native HLS (an old browser): arm 3, the MP4.
        if (src) videoRef.current.src = src;
        return;
      }
      hls = new Hls({ enableWorker: true });
      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        // A fatal hls.js error falls through to the MP4 source — better a
        // single-bitrate playback than a dead player.
        if (data.fatal) {
          hls?.destroy();
          hls = null;
          if (videoRef.current && src) videoRef.current.src = src;
        }
      });
      })
      .catch(() => {
        // The hls.js chunk itself failed to load (offline, a CDN blip, a
        // blocker). Without this the JSX left `src` undefined and the effect
        // owned the source, so the player would sit on its poster forever with
        // no error and no way out. Fall back to the MP4 — arm 3 — so a
        // single-bitrate playback is still possible.
        if (!cancelled && videoRef.current && src) videoRef.current.src = src;
      });
    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [hlsUrl, src, status]);

  const framed = maxHeight != null;
  /**
   * WHAT THE ROW SAYS SHAPES THE BOX; WHAT THE ELEMENT SAYS OVERRULES IT.
   *
   * The declared pair does the job it is here for — a shape before the header
   * arrives, which is the whole point of reserving the box — but it is not
   * authoritative, because the backend stores CODED dimensions and handles
   * rotation nowhere. An iPhone portrait clip recorded as H.264 is coded
   * 1920×1080 with a −90° display matrix; it passes `planFor`'s remux test
   * (`transcode.ts`), `-c copy` preserves the matrix, and 1920×1080 is what
   * `finishProcessing` writes. The browser applies the matrix and renders
   * 1080×1920. Trusting the row there would reserve a full-width landscape
   * box and pillarbox the portrait clip inside it — the exact bug this frame
   * exists to remove, for the exact phone that reported it.
   *
   * `videoWidth`/`videoHeight` are post-rotation, so they are the pair that
   * is never wrong. They arrive with the header `preload="metadata"` is
   * already fetching.
   */
  const declaredRatio = ratioOf(width, height);
  const ratio = measuredRatio ?? declaredRatio ?? FALLBACK_RATIO;
  /**
   * The reserved box. Without a budget only the shape is fixed — the caller's
   * own classes say how big it may get.
   *
   * The ratio travels as a CUSTOM PROPERTY that `FRAME_SHAPE`'s
   * `aspect-[var(--video-aspect)]` reads, rather than as an inline
   * `aspectRatio`, for one reason: jsdom's `cssstyle` silently drops properties
   * it does not implement, and `aspect-ratio` is one of them. Inline, the
   * reservation this whole component rests on would be invisible to every test
   * that tried to pin it. A custom property survives.
   */
  const frameStyle = {
    "--video-aspect": String(ratio),
    ...(framed
      ? { maxHeight: `${maxHeight}px`, maxWidth: `${Math.round(maxHeight * ratio)}px` }
      : {}),
  } as CSSProperties;

  if (status === "processing") {
    return (
      <div
        style={frameStyle}
        className={cn(FRAME_SHAPE, "bg-muted", className)}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-subtle-foreground">
          <div className="w-6 h-6 border-2 border-current/30 border-t-current rounded-full animate-spin" />
          <p className="text-sm">Videoen behandles…</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <FileChip
        name={filename?.trim() || "Video"}
        meta="Videoen kunne ikke behandles"
        action={
          onRemove && (
            <button
              type="button"
              onClick={onRemove}
              disabled={removeBusy}
              className="shrink-0 px-2 py-1 rounded-md text-xs font-medium text-destructive-muted-foreground enabled:hover:bg-destructive-muted disabled:opacity-60 transition-colors"
            >
              {removeBusy ? "Fjerner…" : removeLabel}
            </button>
          )
        }
      />
    );
  }

  // Nothing to play: no MP4 and no HLS, or the element gave up on both.
  if (failed || (src == null && hlsUrl == null)) {
    return (
      <FileChip
        name={filename?.trim() || "Video"}
        meta="Videoen kan ikke afspilles i denne browser — hent den i stedet"
        href={src ?? undefined}
      />
    );
  }

  const player = (
    <video
      ref={videoRef}
      // The MP4 as the element's own source is arm 3: it is what a local video
      // plays, what a native-HLS browser is overridden away from in the effect,
      // and what a fatal hls.js error falls back to. `hlsUrl`-only rows (no MP4)
      // are driven entirely by the effect and set no `src` here.
      src={hlsUrl == null ? (src ?? undefined) : undefined}
      poster={poster}
      controls
      preload="metadata"
      playsInline
      // Where the frame's shape is settled for good — a guess corrected, or a
      // rotated row overruled. Only when the two actually disagree, so a feed
      // of videos whose dimensions were right does not re-render once per clip
      // to arrive at the number it was already given.
      onLoadedMetadata={(e) => {
        const el = e.currentTarget;
        if (!el.videoWidth || !el.videoHeight) return;
        const actual = el.videoWidth / el.videoHeight;
        if (Math.abs(actual - ratio) > 0.01) setMeasuredRatio(actual);
      }}
      onError={() => setFailed(true)}
      className={
        framed
          ? "absolute inset-0 h-full w-full object-contain"
          : cn("w-full max-h-[70vh] rounded-lg bg-black", className)
      }
    />
  );

  if (!framed) return player;

  return (
    <div
      style={frameStyle}
      className={cn(FRAME_SHAPE, "bg-black", className)}
    >
      {player}
      {poster && (
        <>
          {/* WHEN THE STILL IS ACTUALLY THERE. `<video poster>` gives no load
              event of its own, so the same url is requested by an image we can
              listen to — one cache entry, two readers, no second download. */}
          <img
            src={poster}
            alt=""
            aria-hidden
            className="hidden"
            ref={(el) => {
              // A poster already in cache can finish before React attaches
              // `onLoad`, which would leave the shimmer up for good.
              if (el?.complete) setPosterLoaded(true);
            }}
            onLoad={() => setPosterLoaded(true)}
            // A missing still is not a reason to shimmer forever; the black
            // backdrop IS the final state for such a clip.
            onError={() => setPosterLoaded(true)}
          />
          <div
            aria-hidden
            data-testid="video-poster-shimmer"
            className={cn(
              "pointer-events-none absolute inset-0 bg-muted transition-opacity duration-300",
              posterLoaded ? "opacity-0" : "animate-pulse"
            )}
          />
        </>
      )}
    </div>
  );
}
